import { CHECKLISTS } from "./checklists";
import { TOURS } from "./tours";
import type {
  AwaitTourStep,
  Audience,
  ChecklistDefinition,
  TourDefinition,
  TourStep,
} from "./types";

const isVisibleForAudience = (audiences: Audience[], audience: Audience): boolean => {
  if (audience === "admin") {
    return audiences.includes("creator") || audiences.includes("admin");
  }
  return audiences.includes(audience);
};

export const isAwaitStep = (step: TourStep): step is AwaitTourStep => step.kind === "await";

const isStepVisibleForAudience = (step: TourStep, audience: Audience | null): boolean =>
  isAwaitStep(step) || !step.audiences || (audience !== null && step.audiences.includes(audience));

const audienceTours = new Map<string, TourDefinition>();

export const getTour = (id: string, audience: Audience | null): TourDefinition | undefined => {
  const tour = TOURS.find((candidate) => candidate.id === id);
  if (!tour || tour.steps.every((step) => isStepVisibleForAudience(step, audience))) {
    return tour;
  }
  const key = `${id}:${audience ?? ""}`;
  const cached = audienceTours.get(key);
  if (cached) {
    return cached;
  }
  const filtered = {
    ...tour,
    steps: tour.steps.filter((step) => isStepVisibleForAudience(step, audience)),
  };
  audienceTours.set(key, filtered);
  return filtered;
};

export const getStepPosition = (
  tour: TourDefinition,
  stepIndex: number,
  skippedSteps: number[] = [],
): { current: number; total: number } => {
  const firstShownIndex = tour.steps.findIndex(
    (step, index) => !isAwaitStep(step) && !skippedSteps.includes(index),
  );
  const counts = (step: TourDefinition["steps"][number], index: number): boolean =>
    !isAwaitStep(step) && !(skippedSteps.includes(index) && index < firstShownIndex);
  const shownSteps = tour.steps.filter(counts);
  const shownUpTo = tour.steps.slice(0, stepIndex + 1).filter(counts);
  return { current: Math.max(1, shownUpTo.length), total: shownSteps.length };
};

export const getToursForAudience = (audience: Audience): TourDefinition[] =>
  TOURS.filter((tour) => !tour.hidden && isVisibleForAudience(tour.audiences, audience)).map(
    (tour) => getTour(tour.id, audience) ?? tour,
  );

export type BranchingParent = { id: string; stepIndex: number };

export const getBranchingParent = (
  tourId: string,
  audience: Audience | null,
): BranchingParent | null => {
  for (const tour of TOURS) {
    const stepIndex = tour.steps.findIndex(
      (step) => !isAwaitStep(step) && step.branchOn?.tourId === tourId,
    );
    if (stepIndex !== -1 && (audience === null || isVisibleForAudience(tour.audiences, audience))) {
      return { id: tour.id, stepIndex };
    }
  }
  return null;
};

export const findChecklistForAudience = (
  checklists: ChecklistDefinition[],
  audience: Audience,
): ChecklistDefinition | undefined =>
  checklists.find((checklist) => checklist.audience === audience);

export const getChecklistForAudience = (audience: Audience): ChecklistDefinition | undefined =>
  findChecklistForAudience(CHECKLISTS, audience);

export { CHECKLISTS, TOURS };
export type { Audience, ChecklistDefinition, TourDefinition };
